import type { SilencePresetId } from '../audio/silence';
import type { AspectPresetId, FitMode, ResolutionId } from '../geometry/framing';
import type { TimeRange } from '../time/ranges';
import type { Chapter } from '../captions/chapters';
import type { Track } from '../tracking/track';
import { DEFAULT_CAPTION_SETTINGS, type CaptionSettings } from '../captions/types';
import { DEFAULT_TELEMETRY_SETTINGS, type TelemetrySettings } from '../telemetry/overlay';
import { DEFAULT_ZONE_SETTINGS, type ZoneSettings } from '../telemetry/zones';
import { DEFAULT_CAMERA_SETTINGS, type CameraSettings } from '../camera/layer';
import { DEFAULT_ZOOM_SETTINGS, type ZoomSettings } from '../screen/zoom';
import type { Layer } from './layers';
import { DEFAULT_OUTPUT, type OutputFormatId } from './output-format';
import type { PlacementId } from './placement';
import { DEFAULT_TRANSITION, type TransitionSettings } from './transition';
import type { AssetId, Clip } from './sequence';
import type { SpeedRange } from './speed';
import { parseStickerImage, parseStickerPlacement, parseStickerSource, type StickerImage, type StickerPlacement, type StickerSource } from './sticker';

export type MaskStyle = 'blur' | 'pixelate' | 'emoji' | 'solid';
export const MASK_STYLES: readonly MaskStyle[] = ['blur', 'pixelate', 'emoji', 'solid'];

export type QualityId = 'small' | 'balanced' | 'high';
export const QUALITY_IDS: readonly QualityId[] = ['small', 'balanced', 'high'];

/** An area the person drew (or a notification found on screen), in the time and picture of the video it was drawn on. */
export interface ManualTrack extends Track {
  /** The video it belongs to; the opened video when absent. */
  readonly assetId?: AssetId;
}

export const EMOJI_CHOICES = ['😎', '🙂', '🫥', '🤖', '🐱', '⭐', '🔒', '❤️'] as const;

export interface PrivacySettings {
  readonly enabled: boolean;
  readonly style: MaskStyle;
  readonly emoji: string;
  /** What the emoji style draws: the emoji, or the person's own picture (`image`). */
  readonly sticker: StickerSource;
  /** The person's own picture; kept while an emoji is chosen, so going back to it is one tap. */
  readonly image: StickerImage | null;
  /** Where the picture sits on a face (plates and areas are always covered whole). */
  readonly placement: StickerPlacement;
  /** 0..1 — blur radius / pixel size. */
  readonly strength: number;
  /** Extra margin around detections (0.3 = 30 % larger). */
  readonly padding: number;
  /**
   * Privacy first: every detected face/plate is hidden by default. Users opt
   * individual tracks *out* (e.g. their own face) instead of opting in.
   */
  readonly revealedTrackIds: readonly string[];
  /** Regions the user drew (and the app followed); always hidden while privacy is on. */
  readonly manualTracks: readonly ManualTrack[];
}

export interface FormatSettings {
  readonly aspect: AspectPresetId;
  readonly fit: FitMode;
  readonly resolution: ResolutionId;
  /** Normalized crop center used when `fit` is `crop`. */
  readonly focus: { readonly x: number; readonly y: number };
  /** Smart reframing: the crop follows the main face instead of staying at `focus`. */
  readonly followSubject: boolean;
}

export interface AudioSettings {
  readonly muted: boolean;
  /** Normalize loudness to the social media standard (-14 LUFS). */
  readonly normalize: boolean;
  /** Wind rumble filter + RNNoise speech enhancement. */
  readonly denoise: boolean;
}

/**
 * The whole edit as plain, serializable data. The source file is never modified;
 * export renders `source + recipe`. Keeping this a pure value makes it trivially
 * testable, undoable and shareable as a reusable preset.
 */
export interface EditRecipe {
  readonly trim: TimeRange | null;
  /** Parts removed by hand (retakes from a recording), in source time. */
  readonly cuts: readonly TimeRange[];
  /** Parts played faster, in source time. */
  readonly speeds: readonly SpeedRange[];
  readonly silence: { readonly enabled: boolean; readonly preset: SilencePresetId };
  /** Cut hesitation sounds ("ııı", "eee", "um"); as strict as the silence setting. */
  readonly fillers: { readonly enabled: boolean };
  readonly format: FormatSettings;
  readonly privacy: PrivacySettings;
  readonly audio: AudioSettings;
  readonly captions: CaptionSettings;
  /** Chapters of the finished video (source times), from the captions or added by hand. */
  readonly chapters: readonly Chapter[];
  /** Speed/altitude gauge drawn from the camera's GPS data. */
  readonly telemetry: TelemetrySettings;
  /** Hiding the time spent near private places (needs the camera's GPS). */
  readonly zones: ZoneSettings;
  /** Where a screen recording's camera goes (only used when the video has one). */
  readonly camera: CameraSettings;
  /** Automatic zoom into where a screen recording's activity is. */
  readonly zoom: ZoomSettings;
  /** The clips the edit plays, in order; empty means the opened video, whole. */
  readonly sequence: { readonly clips: readonly Clip[] };
  /** Videos, pictures, sounds and words on top of the edit, bottom to top. */
  readonly layers: readonly Layer[];
  /** What happens where clips meet. */
  readonly transition: TransitionSettings;
  readonly quality: QualityId;
  /** What the edit is saved as. */
  readonly output: OutputFormatId;
  /** "Keep it under N MB" for videos (`size-target.ts`), or null for no limit. */
  readonly sizeLimit: number | null;
  /** Where the video is going (`placement.ts`): its length is checked against it; null when it isn't fitted for one. */
  readonly placement: PlacementId | null;
}

export const DEFAULT_PRIVACY: PrivacySettings = {
  enabled: true,
  style: 'blur',
  emoji: '😎',
  sticker: 'emoji',
  image: null,
  placement: 'cover',
  strength: 0.7,
  padding: 0.35,
  revealedTrackIds: [],
  manualTracks: [],
};

/** Privacy settings saved by any version (or a hand-edited file): the picture is checked, anything missing gets its default. */
export function parsePrivacy(value: unknown): PrivacySettings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<PrivacySettings>;
  const merged = { ...DEFAULT_PRIVACY, ...raw };
  return {
    ...merged,
    style: MASK_STYLES.includes(merged.style) ? merged.style : DEFAULT_PRIVACY.style,
    sticker: parseStickerSource(merged.sticker),
    image: parseStickerImage(merged.image),
    placement: parseStickerPlacement(merged.placement),
  };
}

export function createDefaultRecipe(): EditRecipe {
  return {
    trim: null,
    cuts: [],
    speeds: [],
    silence: { enabled: false, preset: 'balanced' },
    fillers: { enabled: false },
    format: { aspect: 'original', fit: 'crop', resolution: 'source', focus: { x: 0.5, y: 0.5 }, followSubject: false },
    privacy: DEFAULT_PRIVACY,
    audio: { muted: false, normalize: true, denoise: false },
    captions: DEFAULT_CAPTION_SETTINGS,
    chapters: [],
    telemetry: DEFAULT_TELEMETRY_SETTINGS,
    zones: DEFAULT_ZONE_SETTINGS,
    camera: DEFAULT_CAMERA_SETTINGS,
    zoom: DEFAULT_ZOOM_SETTINGS,
    sequence: { clips: [] },
    layers: [],
    transition: DEFAULT_TRANSITION,
    quality: 'balanced',
    output: DEFAULT_OUTPUT,
    sizeLimit: null,
    placement: null,
  };
}

export const isTrackHidden = (privacy: PrivacySettings, trackId: string): boolean =>
  privacy.enabled && !privacy.revealedTrackIds.includes(trackId);

export const addManualTrack = (privacy: PrivacySettings, track: ManualTrack): PrivacySettings => ({
  ...privacy,
  enabled: true,
  manualTracks: [...privacy.manualTracks.filter((t) => t.id !== track.id), track],
});

export const removeManualTrack = (privacy: PrivacySettings, trackId: string): PrivacySettings => ({
  ...privacy,
  manualTracks: privacy.manualTracks.filter((t) => t.id !== trackId),
});

export function setTrackRevealed(privacy: PrivacySettings, trackId: string, revealed: boolean): PrivacySettings {
  const others = privacy.revealedTrackIds.filter((id) => id !== trackId);
  return { ...privacy, revealedTrackIds: revealed ? [...others, trackId] : others };
}
